import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { Box, Typography, useTheme, alpha } from '@mui/material'
import UpArrow from '../../assets/icons/UpPointer.tsx'
import { green, red } from '../../assets/colors'

export type RangeBarStaticProps = {
    open: number
    dayLow: number
    dayHigh: number
    weekLow: number
    weekHigh: number
    show52WkRange?: boolean
    hideDayLabels?: boolean
    initialLtp: number
}

export type RangeBarHandle = {
    setLtp: (ltp: number) => void
}

const getPosition = (value: number, min: number, max: number) => {
    if (max <= min) return 50
    if (isNaN(value) || !isFinite(value)) return 50
    const clampedValue = Math.max(min, Math.min(max, value))
    const pos = ((clampedValue - min) / (max - min)) * 100
    return Math.min(100, Math.max(0, pos))
}

const formatNumber = (val: number) =>
    val.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

const RangeBar = forwardRef<RangeBarHandle, RangeBarStaticProps>(function RangeBar(
    { open, dayLow, dayHigh, weekLow, weekHigh, show52WkRange = true, hideDayLabels = false, initialLtp },
    ref
) {
    const theme = useTheme()
    const isDarkTheme = theme.palette.mode === 'dark'
    const barBgColor = isDarkTheme ? alpha(theme.palette.grey[700], 0.5) : theme.palette.grey[300]
    const pointerColor = isDarkTheme ? theme.palette.common.white : theme.palette.common.black

    const dayPointerRef = useRef<HTMLElement | null>(null)
    const weekPointerRef = useRef<HTMLElement | null>(null)
    const daySegmentRef = useRef<HTMLDivElement | null>(null)
    const weekSegmentRef = useRef<HTMLDivElement | null>(null)
    const weekOffsetRef = useRef<HTMLSpanElement | null>(null)

    const shouldShowDayPointer = dayHigh > 0 || dayLow > 0
    const shouldShowWeekPointer = weekHigh > 0 || weekLow > 0
    const dayLowPosWeek = getPosition(dayLow, weekLow, weekHigh)
    const dayHighPosWeek = getPosition(dayHigh, weekLow, weekHigh)
    const weekSegmentLeft = Math.min(dayLowPosWeek, dayHighPosWeek)
    const weekSegmentWidth = Math.max(dayHighPosWeek - dayLowPosWeek, 0.5)
    const weekLeftRadius = weekSegmentLeft <= 0.1 ? '3px' : '0'
    const weekRightRadius = weekSegmentLeft + weekSegmentWidth >= 99.9 ? '3px' : '0'

    const applyLtp = useCallback(
        (ltp: number) => {
            const effectiveOpen = open > 0 ? open : ltp
            const ltpPosDay = getPosition(ltp, dayLow, dayHigh)
            const openPosDay = getPosition(effectiveOpen, dayLow, dayHigh)
            const ltpPosWeek = getPosition(ltp, weekLow, weekHigh)
            const isUp = ltp >= effectiveOpen
            const successColor = isUp ? green : red

            if (daySegmentRef.current) {
                if (Math.abs(ltpPosDay - openPosDay) > 0.5) {
                    const leftPos = Math.min(openPosDay, ltpPosDay)
                    const rightPos = Math.max(openPosDay, ltpPosDay)
                    const leftRadius = leftPos <= 0.1 ? '3px' : '0'
                    const rightRadius = rightPos >= 99.9 ? '3px' : '0'
                    daySegmentRef.current.style.display = 'block'
                    daySegmentRef.current.style.left = `${leftPos}%`
                    daySegmentRef.current.style.width = `${rightPos - leftPos}%`
                    daySegmentRef.current.style.backgroundColor = successColor
                    daySegmentRef.current.style.borderRadius = `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`
                } else {
                    daySegmentRef.current.style.display = 'none'
                }
            }

            if (dayPointerRef.current) {
                dayPointerRef.current.style.left = `${ltpPosDay}%`
            }
            if (weekPointerRef.current) {
                weekPointerRef.current.style.left = `${ltpPosWeek}%`
            }
            if (weekSegmentRef.current) {
                weekSegmentRef.current.style.backgroundColor = successColor
            }
            if (weekOffsetRef.current) {
                weekOffsetRef.current.textContent =
                    weekHigh > 0 && ltp > 0 ? `${formatNumber(((ltp - weekHigh) / weekHigh) * 100)}% from 52W high` : ''
            }
        },
        [open, dayLow, dayHigh, weekLow, weekHigh]
    )

    useImperativeHandle(ref, () => ({ setLtp: applyLtp }), [applyLtp])

    useLayoutEffect(() => {
        applyLtp(initialLtp)
    }, [initialLtp, applyLtp])

    return (
        <Box display="flex" flexDirection="column" gap={hideDayLabels ? 0 : 3} width="100%" py={0} borderRadius={1}>
            <Box>
                {!hideDayLabels && (
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.primary" className="font-feature-num">
                            {formatNumber(dayLow)}{' '}
                            <Box component="span" color="text.tertiary">Low</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Day's Range
                        </Typography>
                        <Typography variant="caption" color="text.primary" className="font-feature-num">
                            <Box component="span" color="text.tertiary">High</Box>{' '}
                            {formatNumber(dayHigh)}
                        </Typography>
                    </Box>
                )}
                <Box
                    position="relative"
                    height={6}
                    bgcolor={barBgColor}
                    borderRadius={3}
                    mb={hideDayLabels ? 1.5 : 2}
                >                    <Box
                        ref={daySegmentRef}
                        position="absolute"
                        height="100%"
                        sx={{ display: 'none' }}
                    />
                    {shouldShowDayPointer && (
                        <UpArrow
                            ref={dayPointerRef}
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: 6,
                                fontSize: 12,
                                color: pointerColor,
                                transform: 'translateX(-50%)'
                            }}
                        />
                    )}
                </Box>
            </Box>

            {show52WkRange && (
                <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.primary">
                            {formatNumber(weekLow)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            52 Week Range
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                            {formatNumber(weekHigh)}
                        </Typography>
                    </Box>
                    <Box position="relative" height={6} bgcolor={barBgColor} borderRadius={3} mb={2}>
                        <Box
                            ref={weekSegmentRef}
                            position="absolute"
                            left={`${weekSegmentLeft}%`}
                            width={`${weekSegmentWidth}%`}
                            height="100%"
                            bgcolor={green}
                            sx={{
                                borderRadius: `${weekLeftRadius} ${weekRightRadius} ${weekRightRadius} ${weekLeftRadius}`,
                                minWidth: dayLowPosWeek === dayHighPosWeek ? '2px' : 'auto'
                            }}
                        />
                        {shouldShowWeekPointer && (
                            <UpArrow
                                ref={weekPointerRef}
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 6,
                                    fontSize: 12,
                                    color: pointerColor,
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        )}
                    </Box>
                    <Typography
                        ref={weekOffsetRef}
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        textAlign="center"
                        className="font-feature-num"
                    />
                </Box>
            )}
        </Box>
    )
})

/**
 * Structure-only stand-in with RangeBar's exact box model, for shimmer placeholders
 * (SecurityRange while its bounds load, and the overview skeleton). Keep in step with RangeBar.
 */
export const RangeBarSkeleton = ({
    show52WkRange = false,
    hideDayLabels = false
}: Pick<RangeBarStaticProps, 'show52WkRange' | 'hideDayLabels'>) => (
    <Box display="flex" flexDirection="column" gap={hideDayLabels ? 0 : 3} width="100%" py={0}>
        <Box>
            {!hideDayLabels && (
                <Box display="flex" justifyContent="space-between" alignItems="center" height={20} mb={0.5}>
                    <Box width={72} height={12} />
                    <Box width={64} height={12} />
                    <Box width={72} height={12} />
                </Box>
            )}
            <Box height={6} borderRadius={3} mb={hideDayLabels ? 1.5 : 2} />
        </Box>
        {show52WkRange && (
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" height={20} mb={0.5}>
                    <Box width={56} height={12} />
                    <Box width={80} height={12} />
                    <Box width={56} height={12} />
                </Box>
                <Box height={6} borderRadius={3} mb={2} />
                <Box display="flex" justifyContent="center" alignItems="center" height={20}>
                    <Box width={132} height={12} />
                </Box>
            </Box>
        )}
    </Box>
)

export default React.memo(RangeBar)
